import { Compass, BarChart3, Rocket, Wallet } from "lucide-react";
import { CUSTOM_DELIVERABLES } from "@/lib/measurement";
import { STATUS_LAUNCH } from "@/lib/status";
import {
  PLANS,
  COMMISSION_PERCENT,
  FOUNDER_MONTHS,
  FOUNDER_UNTIL,
  EXTRA_SPONSORSHIP_FEE,
  TRIAL_DAYS,
  euro,
  pricePair,
  savingOf,
} from "@/lib/pricing";
import { ROUTE_DEMO, ROUTE_WAITLIST } from "@/lib/brand";

/**
 * Häufige Fragen an einer Stelle, nach Themen gruppiert. Die Seite
 * „Häufige Fragen“ zeigt alle Gruppen, die Preisseite die Gruppe „preise“
 * plus die Frage mit `id: "warteliste-kosten"`, die Warteliste nur die
 * Gruppe „start“. `link` hängt unter die Antwort einen Verweis an.
 */

const [CLUB_PLAN, COMPANY_PLAN] = PLANS;

export const FAQ_GROUPS = [
  {
    id: "plattform",
    icon: Compass,
    title: "Plattform",
    lead: "Was MatchGate ist und wie Verein und Unternehmen darüber zueinanderfinden.",
    items: [
      {
        q: "Was ist MatchGate?",
        a: "Eine Plattform, über die Sportvereine und Unternehmen für Sponsoring zueinanderfinden und die Partnerschaft anschließend belegen. Vereine legen ein Profil an und können Projekte ausschreiben, Unternehmen finden passende Vereine über Suche und MatchScore. Kommt ein Sponsoring zustande, wird es auf der Plattform eingerichtet, und ein Dashboard zeigt über die ganze Laufzeit, was daraus wird.",
      },
      {
        q: "Für welche Vereine und Unternehmen ist MatchGate gedacht?",
        a: "Für Vereine aller Sportarten, Ligen und Größen, vom Breiten- bis zum Leistungssport. Und für Unternehmen, die in ihrer Region sponsern wollen, vom Handwerksbetrieb bis zum Mittelstand.",
      },
      {
        q: "Wie finden Verein und Unternehmen zueinander?",
        a: "Auf zwei Wegen, die beide gleich viel wert sind: Unternehmen filtern nach Region, Sportart, Zielgruppe, Reichweite und Projektart, Vereine nach Branche, Region und Themen. Dazu vergleicht das Matching die Angaben beider Seiten und drückt die Übereinstimmung als MatchScore aus. Der Erstkontakt läuft über den integrierten Chat.",
      },
      {
        q: "Was ist der MatchScore?",
        a: "Eine Zahl, die zeigt, wie gut die Angaben eines Vereins oder Projekts zu den Kriterien eines Unternehmens passen. Er nimmt euch die Vorauswahl ab, ersetzt aber nicht das Gespräch. Welche Angaben einfließen, legen wir offen. Eine bessere Platzierung gegen Geld gibt es nicht.",
      },
      {
        q: "Müssen Vereine Projekte anlegen?",
        a: "Nein. Projekte sind ein zusätzliches Angebot. Ihr könnt euch auf ein allgemeines Sponsoring beschränken oder zusätzlich einzelne Vorhaben ausschreiben. Beides funktioniert, auch nebeneinander.",
      },
      {
        q: "Was passiert, wenn ein Sponsoring zustande kommt?",
        a: "Beide Seiten richten es auf der Plattform ein: welche Leistungen vereinbart sind, welche QR-Codes und Links dazugehören und worauf das Unternehmen den größten Wert legt. Beide bestätigen, dann läuft das Dashboard über die gesamte Laufzeit mit.",
      },
    ],
  },
  {
    id: "start",
    icon: Rocket,
    title: "Start und Warteliste",
    lead: "Wann es losgeht und was ein Platz auf der Warteliste bedeutet.",
    items: [
      {
        q: "Ab wann kann ich MatchGate nutzen?",
        a: `MatchGate startet ${STATUS_LAUNCH.in}. Bis dahin könnt ihr in der Demo ausprobieren, wie ein Sponsoring auf der Plattform aussieht, und euch auf der Warteliste einen Platz vormerken. Wer dort steht, erfährt als Erstes, wann es losgeht.`,
        link: { label: "Zur Warteliste", to: ROUTE_WAITLIST },
      },
      {
        id: "warteliste-kosten",
        q: "Verpflichtet mich die Warteliste zu etwas?",
        a: "Nein. Der Eintrag ist kostenlos und unverbindlich, ein Abo schließt ihr damit nicht ab. Wenn MatchGate startet, entscheidet ihr in Ruhe, ob ihr dabei seid.",
      },
      {
        q: "Was passiert mit meinen Angaben auf der Warteliste?",
        a: "Wir nutzen sie nur, um euch zum Start zu benachrichtigen, und wenn ihr das angekreuzt habt, um euch für einen Test vor dem Start anzusprechen. Weitergegeben werden sie nicht. Abmelden könnt ihr euch jederzeit mit einer kurzen Mail, danach löschen wir eure Angaben.",
        link: { label: "Zur Datenschutzerklärung", to: "/datenschutz" },
      },
    ],
  },
  {
    id: "auswertung",
    icon: BarChart3,
    title: "Auswertung",
    lead: "Was gemessen wird, woher die Zahlen kommen und wie viel Arbeit das macht.",
    items: [
      {
        q: "Was genau wird bei einem Sponsoring ausgewertet?",
        a: "Alles, was beim Einrichten vereinbart wurde: von Nachweisen für Bande und Durchsage über Zuschauer und Aufrufe von Beiträgen bis zu QR-Scans, Klicks und den Ergebnissen, die das Unternehmen selbst meldet, etwa eingelöste Gutscheine oder Bewerbungen.",
      },
      {
        q: "Was ist, wenn wir etwas vereinbaren, das in keiner Vorlage steht?",
        a: CUSTOM_DELIVERABLES,
      },
      {
        q: "Woran erkenne ich, wie verlässlich eine Zahl ist?",
        a: "Jede Zahl trägt ihre Herkunft. Gemessen heißt: MatchGate hat sie selbst erfasst oder über eine Schnittstelle übernommen. Angabe heißt: Verein oder Unternehmen haben sie eingetragen, mit Kamera-Symbol, wenn ein Foto oder Dokument als Beleg vorliegt. Schätzung heißt: Sie ist aus Angaben hochgerechnet, und der Rechenweg steht dabei.",
      },
      {
        q: "Wie viel Arbeit macht das für den Verein?",
        a: "Wenig. Was sich automatisch erfassen lässt, läuft automatisch, etwa Beiträge, Scans und Klicks. Was nur der Verein weiß, wie Zuschauer oder die Durchsage, trägt er nach dem Spiel über einen Link in wenigen Sekunden ein. Das geht auch für Helfer ohne eigenes Konto.",
      },
      {
        q: "Wer sieht welche Zahlen?",
        a: "Verein und Unternehmen sehen dasselbe Dashboard. Ergebnisse, die nur das Unternehmen kennt, etwa eingelöste Gutscheine oder Bewerbungen, sieht der Verein nur, wenn das Unternehmen sie freigibt.",
      },
      {
        q: "Werden Daten von Fans und Zuschauern erfasst?",
        a: "Gezählt, nicht verfolgt: Scans und Klicks werden gezählt, ohne dass IP-Adressen gespeichert oder persönliche Profile angelegt werden. MatchGate weiß, wie oft ein Code gescannt wurde, aber nicht, von wem.",
      },
      {
        q: "Kann ich mir das vorher ansehen?",
        a: "Ja. In der Demo stellt ihr ein Beispiel-Sponsoring zusammen, seht das Dashboard und tragt als Verein ein Heimspiel ein. Ohne Anmeldung.",
        link: { label: "Zur Demo", to: ROUTE_DEMO },
      },
    ],
  },
  {
    id: "preise",
    icon: Wallet,
    title: "Preise",
    lead: "Abo, Provision und was davon im Preis enthalten ist.",
    items: [
      {
        q: "Was kostet MatchGate?",
        a: `Vereine zahlen ${pricePair(CLUB_PLAN.regular)}, Unternehmen ${pricePair(
          COMPANY_PLAN.regular
        )}. Im Unternehmens-Abo ist ein Sponsoring enthalten, jedes weitere kostet beim Einrichten einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}. Wer bis zum ${FOUNDER_UNTIL} abschließt, zahlt ${FOUNDER_MONTHS} Monate lang den Gründerpreis. Dazu kommt eine Provision von ${COMMISSION_PERCENT} %, aber nur, wenn über MatchGate tatsächlich ein Vertrag zustande kommt.`,
      },
      {
        q: "Warum sind das geplante Preise?",
        a: "Weil MatchGate noch nicht gestartet ist. Wir zeigen schon jetzt, womit ihr rechnen könnt, statt euch am Starttag zu überraschen. Bis dahin kann sich noch etwas ändern. Verbindlich ist, was bei eurem Abschluss gilt, und wer auf der Warteliste steht, erfährt Änderungen zuerst.",
      },
      {
        q: "Was ist der Gründerpreis?",
        a: `Wer bis zum ${FOUNDER_UNTIL} abschließt, zahlt ${FOUNDER_MONTHS} Monate lang weniger: Vereine ${pricePair(
          CLUB_PLAN.founder
        )}, Unternehmen ${pricePair(
          COMPANY_PLAN.founder
        )}. Danach gilt der reguläre Preis. Wer früh dabei ist, geht mit uns ins Risiko des Anfangs und hilft, die Plattform zu formen.`,
      },
      {
        q: "Was kostet ein weiteres Sponsoring?",
        a: `Für Unternehmen ist ein Sponsoring im Abo enthalten. Jedes weitere kostet einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}, fällig beim Einrichten, und läuft dann über die vereinbarte Laufzeit mit QR-Codes, Links und Dashboard. Endet das enthaltene Sponsoring, wird der Platz wieder frei, das nächste ist dann ohne Aufpreis dabei. Verlängert ihr ein Sponsoring mit demselben Partner, fällt die Gebühr nicht erneut an. Für Vereine gibt es keinen Aufschlag, egal wie viele Sponsorings sie einrichten.`,
      },
      {
        q: "Warum zahlen Unternehmen je Sponsoring und Vereine nicht?",
        a: "Weil der Nutzen für Unternehmen mit jedem Sponsoring wächst: Jedes bekommt eigene QR-Codes und Links, ein eigenes Dashboard und am Ende einen Bericht. So zahlt ein Unternehmen mit einem Sponsoring weniger als eines mit zehn. Vereine haben dagegen schnell zehn bis dreißig Sponsoren, ein Aufschlag würde genau die treffen, die MatchGate am meisten nutzen.",
      },
      {
        q: "Kann man MatchGate vorher testen?",
        a: `Ja. Nach dem Start könnt ihr MatchGate ${TRIAL_DAYS} Tage kostenlos testen, ohne Zahlungsdaten anzugeben. Schließt ihr danach kein Abo ab, endet der Zugang automatisch und es entstehen keine Kosten. Schon jetzt zeigt euch die Demo, wie ein Sponsoring auf MatchGate aussieht.`,
        link: { label: "Zur Demo", to: ROUTE_DEMO },
      },
      {
        q: "Sind alle Funktionen im Abo enthalten?",
        a: "Ja. Es gibt keine Funktionsstufen und keine Zusatzpakete. Profile, Projekte, Suche, Matching, Analytics-Dashboard und Chat nutzen beide Seiten. Pitch-Generator und Media-Kit-Templates sind Werkzeuge für die Vereinsarbeit und deshalb nur im Vereins-Abo.",
      },
      {
        q: "Wofür genau fällt die Provision an?",
        a: `Für die erfolgreiche Vermittlung. Sie beträgt ${COMMISSION_PERCENT} % und wird fällig, wenn über MatchGate ein Sponsoringvertrag geschlossen wird. Führen Gespräche zu keinem Abschluss, entstehen keine weiteren Kosten. Für Sponsorings, die ihr mitbringt, fällt sie nicht an.`,
      },
      {
        q: "Können wir ein bestehendes Sponsoring auf MatchGate übertragen?",
        a: `Ja, und zwar ohne Provision, auch bei späteren Verlängerungen. Als bestehend gilt, was ihr vereinbart hattet, bevor ihr über MatchGate in Kontakt gekommen seid. Für Unternehmen gilt dabei dasselbe wie bei jedem Sponsoring: Eines ist im Abo enthalten, jedes weitere kostet beim Einrichten einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}. Für Vereine bleibt es beim Abo.`,
      },
      {
        q: "Lohnt sich die jährliche Zahlung?",
        a: `Ja. Zum regulären Preis sparen Vereine rund ${euro(savingOf(
          CLUB_PLAN.regular
        ))} gegenüber zwölf Monatszahlungen, Unternehmen rund ${euro(savingOf(
          COMPANY_PLAN.regular
        ))}. Zum Gründerpreis sind es etwa ${euro(savingOf(
          CLUB_PLAN.founder
        ))} beziehungsweise ${euro(savingOf(COMPANY_PLAN.founder))}.`,
      },
    ],
  },
];

export const FAQ_BY_ID = Object.fromEntries(FAQ_GROUPS.map((g) => [g.id, g]));

